import React, { useCallback, useState } from 'react';

import { Button, Divider, Flex, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconUsers } from '@tabler/icons-react';
import ExpandableSection from 'components/ExpandableSection/ExpandableSection';
import KiloButton from 'components/KiloButton';
import LastCreditAlertModal from 'components/LastCreditAlertModal';
import { useNavigate } from 'react-router-dom';
import { KiloClassRetrieve } from 'types';
import { LastCreditAlertContent } from 'utils/last-credit-alert';

import LateCancelNoShowAthletes from './components/LateCancelAndNoShowAthletes';
import ModalCreateAthleteClass from './components/ModalCreateAthleteClass';
import ReservedAndCheckedInAthletes from './components/ReservedAndCheckedInAthletes';
import WaitlistedAthletes from './components/WaitlistedAthletes';
import { AddAthleteState } from './types';

interface IReservationsFormProps {
  kiloClass: KiloClassRetrieve;
  permission?: boolean;
}

const ReservationsForm = ({ kiloClass, permission = true }: IReservationsFormProps): JSX.Element => {
  const [waitlistOpened, { toggle: handleWaitlistToggle }] = useDisclosure();
  const [lateCancelOpened, { toggle: handleLateCancelToggle }] = useDisclosure();
  const theme = useMantineTheme();
  const navigate = useNavigate();

  const reservedAndCheckedInAthletes = kiloClass.athleteKiloClasses.filter(
    akc => (akc.isCheckedIn || akc.isReserved) && !akc.isLateCancel && !akc.isNoShow,
  );
  const waitlistedAthletes = kiloClass.athleteKiloClasses
    .filter(akc => akc.isWaitlisted)
    .sort((a, b) => a.waitlistOrder! - b.waitlistOrder!);

  const lateCancelNoShowAthletes = kiloClass.athleteKiloClasses.filter(
    akc => akc.isLateCancel || akc.isNoShow,
  );

  const [lastCreditAlert, setLastCreditAlert] = useState<LastCreditAlertContent | null>(null);

  const [addModalOpened, { open: openAddModal, close: closeAddModal }] = useDisclosure(false);
  const [addModalType, setAddModalType] = useState<AddAthleteState>('attended');
  const handleOpenAddModal = (type: AddAthleteState): void => {
    setAddModalType(type);
    openAddModal();
  };

  const redirectToKioskCheckIn = useCallback((): void => {
    navigate(`/kiosk/${kiloClass.kiloClassId}?eventType=class&from=calendar`);
  }, [kiloClass, navigate]);

  return (
    <Stack spacing='xs'>
      <Group grow pb='sm'>
        <Flex direction='row' gap={'md'}>
          <Text fz='lg' fw='600'>
            Reserved
          </Text>
          <KiloButton
            onClick={() => redirectToKioskCheckIn()}
            variant='outline'
            fw={600}
            py={3}
            px={7}
            h={28}
          >
            Kiosk Check In
          </KiloButton>
        </Flex>
        <Group position='right'>
          <IconUsers size={theme.fontSizes.xl} />
          <Text fw='600'>
            Event Size: {reservedAndCheckedInAthletes.length} / {kiloClass.capacity}
          </Text>
        </Group>
      </Group>
      <ReservedAndCheckedInAthletes
        athleteKiloClasses={reservedAndCheckedInAthletes}
        setLastCreditAlert={setLastCreditAlert}
        permission={permission}
        kiloClass={kiloClass}
      />
      <Group position='center' p='lg'>
        <Button
          disabled={!permission}
          leftIcon={<IconPlus size={theme.fontSizes.sm} />}
          onClick={() => handleOpenAddModal('reserved')}
          data-cy='addReservationBtn'
        >
          Reserved Athlete
        </Button>
        <Button
          disabled={!permission}
          leftIcon={<IconPlus size={theme.fontSizes.sm} />}
          onClick={() => handleOpenAddModal('attended')}
        >
          Attended Athlete
        </Button>
      </Group>

      <Divider mt={0} mb='md' mx='-xl' />

      <ExpandableSection title='Waitlist' opened={waitlistOpened} handleToggle={handleWaitlistToggle}>
        <WaitlistedAthletes athleteKiloClasses={waitlistedAthletes} permission={permission} />
        <Group position='center' p='lg'>
          <Button
            disabled={kiloClass.capacity > reservedAndCheckedInAthletes.length || !permission}
            leftIcon={<IconPlus size={theme.fontSizes.sm} />}
            onClick={() => handleOpenAddModal('waitlisted')}
          >
            Waitlisted Athlete
          </Button>
        </Group>
      </ExpandableSection>
      <Divider mt={0} mb='md' mx='-xl' />
      <ExpandableSection
        title='Late Cancel/No Show'
        opened={lateCancelOpened}
        handleToggle={handleLateCancelToggle}
      >
        <LateCancelNoShowAthletes athleteKiloClasses={lateCancelNoShowAthletes} permission={permission} />
      </ExpandableSection>

      <ModalCreateAthleteClass
        opened={addModalOpened}
        handleClose={closeAddModal}
        type={addModalType}
        kiloClassId={kiloClass.kiloClassId}
        setLastCreditAlert={setLastCreditAlert}
      />

      <LastCreditAlertModal alert={lastCreditAlert} onClose={() => setLastCreditAlert(null)} />
    </Stack>
  );
};

export default ReservationsForm;
